import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Link } from 'react-router-dom';
import AgentList from './components/AgentList';
import LuckyDrawOversight from './components/LuckyDrawOversight';
import EditBetModal from './components/EditBetModal';
import DrawCycleManager from './components/DrawCycleManager';
import TelegramBetsView from './components/TelegramBetsView';
import WinnerReveal from './components/WinnerReveal';
import LoginPage from './components/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import SuperAdminLoginPage from './components/SuperAdminLoginPage';
import AdminManagement from './components/AdminManagement';
import AgentLoginPage from './components/AgentLoginPage';
import AgentBetPage from './components/AgentBetPage';

function App() {
  const [superAdmin, setSuperAdmin] = useState(() => {
    const stored = localStorage.getItem('superAdmin');
    return stored ? JSON.parse(stored) : null;
  });

  const [admin, setAdmin] = useState(() => {
    const stored = localStorage.getItem('admin');
    return stored ? JSON.parse(stored) : null;
  });

  const [agent, setAgent] = useState(() => {
    const stored = localStorage.getItem('agent');
    return stored ? JSON.parse(stored) : null;
  });

  // Sync states with localStorage
  useEffect(() => {
    if (superAdmin) localStorage.setItem('superAdmin', JSON.stringify(superAdmin));
    else localStorage.removeItem('superAdmin');
  }, [superAdmin]);

  useEffect(() => {
    if (admin) localStorage.setItem('admin', JSON.stringify(admin));
    else localStorage.removeItem('admin');
  }, [admin]);

  useEffect(() => {
    if (agent) localStorage.setItem('agent', JSON.stringify(agent));
    else localStorage.removeItem('agent');
  }, [agent]);

  // Logout handlers
  const handleSuperAdminLogout = () => setSuperAdmin(null);
  const handleAdminLogout = () => setAdmin(null);
  const handleAgentLogout = () => setAgent(null);

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Routes>
          {/* Admin login */}
          <Route path="/login" element={<LoginWrapper setAdmin={setAdmin} />} />
          
          {/* Telegram Admin login */}
          <Route path="/telegram-admin-login" element={<TelegramAdminLoginWrapper setAdmin={setAdmin} />} />

          {/* Telegram Admin Dashboard - Separate interface */}
          <Route
            path="/telegram-admin/*"
            element={
              admin && admin.role === 'telegram_admin' ? (
                <div className="min-h-screen bg-gray-100">
                  <TelegramAdminDashboard onLogout={handleAdminLogout} />
                  <Routes>
                    <Route path="/" element={<DrawCycleManager />} />
                    <Route path="/draw-cycle" element={<DrawCycleManager />} />
                    <Route path="/telegram-bets" element={<TelegramBetsView />} />
                  </Routes>
                </div>
              ) : (
                <TelegramAdminLoginWrapper setAdmin={setAdmin} />
              )
            }
          />

          {/* Protected routes for regular admins */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Dashboard
                  onSuperAdminLogout={handleSuperAdminLogout}
                  onAdminLogout={handleAdminLogout}
                  onAgentLogout={handleAgentLogout}
                />
                <Routes>
                  <Route path="/agents" element={<AgentList />} />
                  <Route path="/lucky-draw" element={<LuckyDrawOversight />} />
                  <Route path="/edit-bet" element={<EditBetModal />} />
                  <Route path="/draw-cycle" element={<DrawCycleManager />} />
                  <Route path="/winner-reveal" element={<WinnerReveal />} />
                </Routes>
              </ProtectedRoute>
            }
          />

          {/* Super admin login */}
          <Route path="/super-admin-login" element={<SuperAdminLoginWrapper setSuperAdmin={setSuperAdmin} />} />
          <Route path="/login-supag" element={<SuperAdminLoginWrapper setSuperAdmin={setSuperAdmin} />} />
          <Route
            path="/admin-management"
            element={
              superAdmin ? (
                <AdminManagement onLogout={handleSuperAdminLogout} />
              ) : (
                <SuperAdminLoginWrapper setSuperAdmin={setSuperAdmin} />
              )
            }
          />

          {/* Agent login */}
          <Route path="/agent-login" element={<AgentLoginWrapper setAgent={setAgent} />} />
          <Route
            path="/agent-bet"
            element={
              agent ? (
                <AgentBetPage agent={agent} onLogout={handleAgentLogout} />
              ) : (
                <AgentLoginWrapper setAgent={setAgent} />
              )
            }
          />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

function LoginWrapper({ setAdmin }) {
  const navigate = useNavigate();
  return (
    <LoginPage
      onLogin={(admin) => {
        setAdmin(admin);
        navigate('/agents');
      }}
    />
  );
}

function TelegramAdminLoginWrapper({ setAdmin }) {
  const navigate = useNavigate();
  return (
    <TelegramAdminLoginPage
      onLogin={(admin) => {
        setAdmin(admin);
        navigate('/telegram-admin');
      }}
    />
  );
}

function TelegramAdminDashboard({ onLogout }) {
  return (
    <nav className="bg-blue-600 shadow flex gap-6 px-8 py-4 mb-6 text-white">
      <div className="flex items-center gap-2">
        <span className="text-xl">📱</span>
        <span className="font-bold text-lg">Telegram Admin Panel</span>
      </div>
      <Link to="/telegram-admin/draw-cycle" className="text-white font-semibold hover:underline bg-blue-700 px-3 py-1 rounded">
        Daily Draw Manager
      </Link>
      <Link to="/telegram-admin/telegram-bets" className="text-white font-semibold hover:underline bg-blue-700 px-3 py-1 rounded">
        Today's Bets
      </Link>
      <button
        className="ml-auto text-red-200 font-semibold hover:text-white bg-red-600 px-3 py-1 rounded"
        onClick={() => {
          localStorage.removeItem('admin');
          onLogout();
          window.location.href = '/telegram-admin-login';
        }}
      >
        Logout
      </button>
    </nav>
  );
}

function SuperAdminLoginWrapper({ setSuperAdmin }) {
  const navigate = useNavigate();
  return (
    <SuperAdminLoginPage
      onLogin={(admin) => {
        setSuperAdmin(admin);
        navigate('/admin-management');
      }}
    />
  );
}

function AgentLoginWrapper({ setAgent }) {
  const navigate = useNavigate();
  return (
    <AgentLoginPage
      onLogin={(agent) => {
        setAgent(agent);
        navigate('/agent-bet');
      }}
    />
  );
}

// Telegram Admin Login Component
function TelegramAdminLoginPage({ onLogin }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || window.location.origin + '/api';
      const response = await fetch(`${API_BASE_URL}/auth/telegram-admin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();
      
      if (response.ok) {
        onLogin(data.user);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-2xl w-96">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">📱</div>
          <h2 className="text-2xl font-bold text-gray-800">Telegram Admin</h2>
          <p className="text-gray-600">Daily Draw Management</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="telebet_admin"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter password"
              required
            />
          </div>
          
          
          
          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-2 rounded">{error}</div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Logging in...' : 'Login to Telegram Admin'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default App;
